/**
 * t568u: one rule for colouring a sales figure against the previous period, used by every Store sales surface (web
 * desktop and phone, the app). Up is good (green), down is bad (red); for figures where lower is better (refunds,
 * refund count) it flips. Equal (to the whole per cent the change line shows), nothing to compare with, or no figure
 * is neutral. The change text always carries ▲/▼ as well, so colour is never the only signal.
 */

export type SalesTrend = 'up' | 'down' | 'same' | 'none';
export type SalesTone = 'good' | 'bad' | 'neutral';

/** Calm, accessible green/red: #15803d / #b91c1c on white (≥ 4.5:1), lighter shades on dark backgrounds. */
export const SALES_TONE_COLOR: Record<'light' | 'dark', Record<Exclude<SalesTone, 'neutral'>, string>> = {
  light: { good: '#15803d', bad: '#b91c1c' },
  dark: { good: '#4ade80', bad: '#f87171' },
};
/** Up, down, the same (rounds to 0%), or none (no previous figure, or it was 0, or no current figure). */
export function salesTrend(cur: number | null | undefined, prev: number | null | undefined): SalesTrend {
  if (cur == null || prev == null || prev === 0) return 'none';
  const pct = Math.round(((cur - prev) / Math.abs(prev)) * 100);
  return pct === 0 ? 'same' : pct > 0 ? 'up' : 'down';
}

/** From a ready-made change fraction (0.12 = +12%; null = nothing to compare with). */
export function salesTrendFromChange(change: number | null | undefined): SalesTrend {
  if (change == null || !Number.isFinite(change)) return 'none';
  const pct = Math.round(change * 100);
  return pct === 0 ? 'same' : pct > 0 ? 'up' : 'down';
}

export const salesToneOf = (trend: SalesTrend, opts: { lowerIsBetter?: boolean } = {}): SalesTone =>
  trend === 'up' ? (opts.lowerIsBetter ? 'bad' : 'good') : trend === 'down' ? (opts.lowerIsBetter ? 'good' : 'bad') : 'neutral';

/** Good / bad / neutral for a figure against the previous period. */
export const salesTone = (cur: number | null | undefined, prev: number | null | undefined, opts: { lowerIsBetter?: boolean } = {}): SalesTone =>
  salesToneOf(salesTrend(cur, prev), opts);

/** The colour for a tone (null for neutral: use the surface's usual text colour). */
export const salesToneColor = (tone: SalesTone, scheme: 'light' | 'dark' = 'light'): string | null =>
  tone === 'neutral' ? null : SALES_TONE_COLOR[scheme][tone];

/**
 * t568u: hour labels under every bar of a sales-by-hour chart. Each label is the bar's start hour, centred under it,
 * so it matches the tooltip ("13:00 · £4,930.44"). "13:00" when the bar's slot is wide enough, "13" when narrower,
 * and only when even that won't fit, every other hour (the rest blank).
 */
export const HOUR_LABEL_FULL_PX = 30;
export const HOUR_LABEL_SHORT_PX = 13;
export function hourAxisLabel(h: number, slotPx: number, index = h): string {
  if (!(slotPx > 0) || slotPx >= HOUR_LABEL_FULL_PX) return `${String(h).padStart(2, '0')}:00`;
  if (slotPx >= HOUR_LABEL_SHORT_PX) return String(h).padStart(2, '0');
  return index % 2 === 0 ? String(h).padStart(2, '0') : '';
}
